import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Zap, Loader2, ArrowLeft, MailCheck, ArrowRight } from "lucide-react";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.reset_link) setResetLink(data.reset_link);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const relativeLink = resetLink ? resetLink.substring(resetLink.indexOf("/reset-password")) : "";

  return (
    <div className="min-h-screen bg-[#FDF8FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center shadow-lg shadow-pink-500/30"><Zap className="h-6 w-6 text-white" fill="white" /></div>
          <span className="font-display font-extrabold text-2xl">MrktMe</span>
        </Link>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8 animate-fade-up">
          {!sent ? (
            <>
              <h1 className="font-display text-2xl font-extrabold text-slate-900">Forgot password?</h1>
              <p className="text-slate-500 text-sm mt-1">Enter your email and we'll generate a reset link.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input data-testid="forgot-email" required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                {error && <p data-testid="forgot-error" className="text-sm text-rose-600 font-medium">{error}</p>}
                <button data-testid="forgot-submit" disabled={loading} type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-white brand-gradient shadow-lg shadow-pink-500/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60">
                  {loading && <Loader2 className="h-5 w-5 animate-spin" />} Send reset link
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success" className="text-center">
              <div className="h-14 w-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4"><MailCheck className="h-7 w-7 text-green-600" /></div>
              <h1 className="font-display text-2xl font-extrabold text-slate-900">Check your inbox</h1>
              <p className="text-slate-500 text-sm mt-1">If an account exists for <b>{email}</b>, a reset link has been generated.</p>
              {relativeLink && (
                <div className="mt-5 text-left bg-fuchsia-50 border border-fuchsia-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-fuchsia-700 uppercase tracking-wide mb-2">Demo mode — no email configured</p>
                  <Link data-testid="forgot-reset-link" to={relativeLink}
                    className="inline-flex items-center gap-1.5 font-bold text-fuchsia-700 hover:underline break-all text-sm">
                    Continue to reset your password <ArrowRight className="h-4 w-4 shrink-0" />
                  </Link>
                </div>
              )}
            </div>
          )}

          <Link to="/login" data-testid="forgot-back" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-pink-600 font-semibold">
            <ArrowLeft className="h-4 w-4" /> Back to log in
          </Link>
        </div>
      </div>
    </div>
  );
}
